import React, { useEffect, useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { DataTable } from '@/v2/components/DataTable/tasks/components/data-table';
import { DataTableColumnHeader } from '@/v2/components/DataTable/tasks/components/data-table-column-header';
import { format } from 'date-fns';
import { Infinity, Check, Ban } from 'lucide-react';
import { useHistory } from 'react-router-dom';
import useFetch from 'use-http';

import { getQueryString } from '@/components/constants/visitsQueryStringBuilder';
import { useUserState } from '@/components/context/UserContext';
import { InfoButton } from '@/components/ui/shared/InfoButton';

export default function PaymentsTable() {
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const userState = useUserState();

  const {
    get: getPayments,
    response: getPaymentsResponse,
    abort,
  } = useFetch('/UserAccount/Payments');

  const [dateRange] = useState(userState.dateFilter);
  const history = useHistory();

  useEffect(() => {
    let isMounted = true;

    const fetchData = async () => {
      setIsLoading(true);

      const query = getQueryString(userState.espbiEsi);
      const response = await getPayments(query);

      if (!isMounted) return;

      if (getPaymentsResponse.ok && Array.isArray(response)) {
        setData(response);
      } else {
        setData([]);
      }

      setIsLoading(false);
    };

    fetchData();

    return () => {
      isMounted = false;
      abort();
    };
  }, [userState?.espbiEsi, dateRange]);

  return (
    <DataTable
      title="Mano mokėjimai"
      loading={isLoading}
      columns={columns(history)}
      data={data}
      initialColumnFilters={[{ id: 'paymentStatus', value: undefined }]}
      filterableColumns={['visitTitle', 'date', 'paymentStatus']}
      facetedFilters={[
        {
          columnId: 'paymentStatus',
          title: 'Statusas',
          options: [
            { value: undefined, label: 'Visi', icon: Infinity },
            { value: 'Apmokėta', label: 'Apmokėti', icon: Check },
            { value: 'Neapmokėta', label: 'Neapmokėti', icon: Ban },
          ],
        },
      ]}
      onRowClick={(row) =>
        history.push('/userAccount/payments/details', { visit: row })
      }
    />
  );
}

const columns = (history) => [
  {
    accessorKey: 'visitTitle',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="PASLAUGA/GYDYTOJAS" />
    ),
    cell: ({ row }) => (
      <div className="font-medium leading-tight capitalize">
        <div>{row.original.visitTitle}</div>
        <div className="text-muted-foreground text-sm capitalize">
          {row.original.location}
        </div>
      </div>
    ),
  },

  {
    accessorKey: 'date',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="DATA" />
    ),
    cell: ({ row }) => (
      <span className="text-muted-foreground">
        {format(new Date(row.original.date), 'yyyy-MM-dd HH:mm')}
      </span>
    ),
    enableSorting: true,
    sortingFn: 'datetime',
  },

  {
    accessorKey: 'totalPrice',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="KAINA" />
    ),
    cell: ({ row }) => <span>{row.original.totalPrice.toFixed(2)} €</span>,
    enableSorting: true,
  },

  {
    accessorKey: 'paymentStatus',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="STATUSAS" />
    ),
    cell: ({ row }) => (
      <Badge variant={row.original.isPaid ? 'success' : 'fail'}>
        {row.original.paymentStatus}
      </Badge>
    ),
    enableSorting: true,
    filterFn: (row, columnId, filterValue) => {
      const value = row.getValue(columnId);
      if (!value || !filterValue) return true;
      return value.toString().toLowerCase() === filterValue.toLowerCase();
    },
  },

  {
    id: 'actions',
    header: '',
    cell: ({ row }) => (
      <InfoButton
        onClick={(row) =>
          history.push('/userAccount/payments/details', { visit: row.original })
        }
      >
        Vizito informacija
      </InfoButton>
    ),
  },
];
